/**
 * AuthContext: Manages user authentication state and provides login/logout.
 * After successful login, logs the activity via auth_log-login (non-blocking).
 */
import React, { createContext, useContext, useEffect, useState } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { callFunction } from "@/lib/http";
import posthog from "posthog-js";

// Extended user metadata interface with tour completion tracking
export interface UserMetadata {
  active_tenant_id?: string;
  first_name?: string;
  last_name?: string;
  has_completed_ams_tour?: boolean;
  ams_tour_version?: string;
  tour_completed_at?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  activeTenantId: string | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  selectTenant: (tenantId: string) => Promise<void>;
  setSessionFromToken: (
    accessToken: string,
    refreshToken?: string
  ) => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTenantId, setActiveTenantId] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    console.log('[AuthContext] Initializing auth context');
    
    // Lazy auth initialization - defer session fetch to not block initial render
    // This prevents the auth edge function from blocking public pages
    const initAuth = async () => {
      try {
        console.log('[AuthContext] Fetching session...');
        const {
          data: { session },
        } = await supabase.auth.getSession();
        console.log('[AuthContext] Session fetched:', session ? 'Valid session' : 'No session');
        setSession(session);
        setUser(session?.user ?? null);
        setActiveTenantId(
          session?.user?.user_metadata?.active_tenant_id || null
        );
      } catch (error) {
        console.error('[AuthContext] Auth initialization error:', error);
      } finally {
        setLoading(false);
        setInitialized(true);
        console.log('[AuthContext] Auth initialization complete');
      }
    };

    // Defer auth check by 100ms to allow page to render first
    const timer = setTimeout(initAuth, 100);

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('[AuthContext] Auth state change:', event, session ? 'Has session' : 'No session');
      setSession(session);
      setUser(session?.user ?? null);
      setActiveTenantId(session?.user?.user_metadata?.active_tenant_id || null);
      setLoading(false);
      setInitialized(true);

      // On sign in, log activity if tenant context exists
      if (event === "SIGNED_IN" && session?.user) {
        const tenantId = session.user.user_metadata?.active_tenant_id;
        const user = session.user;

        if (tenantId) {
          // Non-blocking activity log
          callFunction("auth_log-login", {
            userId: user.id,
            tenantId: tenantId,
          }).catch((error) => {
            console.error("Failed to log login activity:", error);
            // Do not disrupt UX
          });
        }

        // Identify user in PostHog (if analytics enabled)
        if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
          posthog.identify(user.id, {
            email: user.email,
            active_tenant_id: tenantId,
            first_name: user.user_metadata?.first_name,
            last_name: user.user_metadata?.last_name,
          });
        }
      }

      // On sign out, reset PostHog identity
      if (event === "SIGNED_OUT") {
        if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true') {
          posthog.reset();
        }
      }
    });

    return () => {
      clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  };

  const logout = async () => {
    try {
      console.log('[AuthContext] Logging out...');
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('[AuthContext] Logout error:', error);
        // If session is already invalid, clear local state anyway
        if (error.message.includes('session') || error.status === 403) {
          console.log('[AuthContext] Session invalid, clearing local state');
          setSession(null);
          setUser(null);
          setActiveTenantId(null);
          return; // Don't throw, session is already gone
        }
        throw error;
      }
      console.log('[AuthContext] Logout successful');
    } catch (error) {
      console.error('[AuthContext] Logout exception:', error);
      // Clear local state even if logout fails
      setSession(null);
      setUser(null);
      setActiveTenantId(null);
      throw error;
    }
  };

  const selectTenant = async (tenantId: string) => {
    try {
      const response = await callFunction("auth_set-active-tenant", {
        tenant_id: tenantId,
      });

      // Response should contain new tokens
      if (
        response &&
        typeof response === "object" &&
        "access_token" in response
      ) {
        const tokenData = response as {
          access_token: string;
          refresh_token?: string;
        };
        await setSessionFromToken(
          tokenData.access_token,
          tokenData.refresh_token
        );
      } else {
        // Fallback to local state update if token exchange isn't working
        setActiveTenantId(tenantId);
      }

      // Update PostHog user properties with new tenant
      if (import.meta.env.VITE_ENABLE_ANALYTICS === 'true' && user) {
        posthog.setPersonProperties({
          active_tenant_id: tenantId,
        });
        posthog.capture('tenant_switched', {
          tenant_id: tenantId,
        });
      }
    } catch (error) {
      console.error("Failed to select tenant:", error);
      throw error;
    }
  };

  const setSessionFromToken = async (
    accessToken: string,
    refreshToken?: string
  ) => {
    try {
      // Only include refresh_token if it exists and is not empty
      const sessionData: { access_token: string; refresh_token?: string } =
        refreshToken && refreshToken.trim() !== ""
          ? { access_token: accessToken, refresh_token: refreshToken }
          : { access_token: accessToken };

      const { data, error } = await supabase.auth.setSession(
        sessionData as any
      );
      if (error) throw error;

      // Session state will be updated via the auth state change listener
    } catch (error) {
      console.error("Failed to set session from token:", error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        activeTenantId,
        login,
        logout,
        selectTenant,
        setSessionFromToken,
        loading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
